import React, { ChangeEvent } from 'react'
import { Button } from '@/components/ui/button'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import { CountryDto } from 'api/Identity/Types/Country'
import { CreateCampaignCountryOverride } from 'api/Trading-Accounts/Types/CreateCampaignCountryOverride'
import {
  CountryOverride,
  MarketingCampaignDetailDto,
} from 'api/Trading-Accounts/Types/MarketingCampaignDetailDto'
import {
  FormBaseButtonContainer,
  FormBaseContainer,
  FormBaseErrorMessage,
  FormBaseFieldBlock,
  FormBaseFieldGroup,
  FormBaseFieldLabel,
  FormBaseRow,
} from 'components/Form/Form.styles'
import { MultiSelect, Option } from 'components/Select'
import { Field, Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

export interface AddCountryOverrideFormValues {
  id: string | undefined
  countryIds: string[]
  tickmillCompanyId: number
  minAmount: number
  enabled: boolean
}

const AddCountryOverrideFormUI: React.FC<FormikProps<AddCountryOverrideFormValues> & OuterProps> = (
  props
) => {
  const {
    countryOverrides,
    handleSubmit,
    setFieldValue,
    setSubmitting,
    isSubmitting,
    values,
    errors,
  } = props

  const handleSubmitForm = () => {
    setSubmitting(true)
    handleSubmit()
  }

  return (
    <FormBaseContainer>
      <Form>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Minimum Amount</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field type='number' name='minAmount' min='0' step='0.01' />
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.minAmount && <FormBaseErrorMessage>{errors.minAmount}</FormBaseErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Tickmill Company</FormBaseFieldLabel>
            <Field
              component='select'
              name='tickmillCompanyId'
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setFieldValue('tickmillCompanyId', event.target.value)
                setFieldValue('countryIds', [])
              }}
            >
              <option value=''>None</option>
              {props.tickmillCompanies.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </Field>
          </FormBaseRow>
          {errors.tickmillCompanyId && (
            <FormBaseErrorMessage>{errors.tickmillCompanyId}</FormBaseErrorMessage>
          )}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Country</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <MultiSelect
                placeholder='Country'
                value={props.values.countryIds}
                onChange={(countryIds: number[]) => {
                  props.setFieldValue('countryIds', countryIds)
                }}
                disabled={!values.tickmillCompanyId}
                fullWidth
              >
                {filterExistingCountries(
                  props.countries,
                  countryOverrides,
                  values.tickmillCompanyId
                ).map((x) => (
                  <Option value={x.id} key={x.id}>
                    {x.name}
                  </Option>
                ))}
              </MultiSelect>
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.countryIds && <FormBaseErrorMessage>{errors.countryIds}</FormBaseErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Enabled</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field name='enabled' type='checkbox' />
            </FormBaseFieldGroup>
          </FormBaseRow>
        </FormBaseFieldBlock>
        <FormBaseButtonContainer>
          <Button disabled={isSubmitting} key='button' type='button' onClick={handleSubmitForm}>
            Submit
          </Button>
        </FormBaseButtonContainer>
      </Form>
    </FormBaseContainer>
  )
}

interface OuterProps {
  data: MarketingCampaignDetailDto
  tickmillCompanies: TickmillCompanyDto[]
  countries: CountryDto[]
  countryOverrides: CountryOverride[]

  onSubmit(values: FormSubmitValues<CreateCampaignCountryOverride[]>): Promise<void>
}

function dataToSubmit(
  values: AddCountryOverrideFormValues,
  countries: CountryDto[]
): FormSubmitValues<CreateCampaignCountryOverride[]> {
  return values.countryIds
    .map((countryId) => countries.find((x) => x.id === countryId))
    .filter((x): x is CountryDto => !!x)
    .map((x) => ({
      countryId: x.id,
      tickmillCompanyId: values.tickmillCompanyId,
      minAmount: values.minAmount,
      enabled: values.enabled,
    }))
}

export const AddCountryOverrideForm = withFormik<OuterProps, AddCountryOverrideFormValues>({
  mapPropsToValues: () => {
    return {
      id: undefined,
      countryIds: [],
      enabled: false,
      minAmount: 0,
      tickmillCompanyId: 0,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values, props.countries))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<AddCountryOverrideFormValues> = {}
    if (values.countryIds.length <= 0) {
      errors.countryIds = 'required'
    }
    if (values.minAmount < 0) {
      errors.minAmount = 'Must be greater than 0.'
    }
    if (!values.tickmillCompanyId) {
      errors.tickmillCompanyId = 'required'
    }
    return errors
  },
  enableReinitialize: true,
})(AddCountryOverrideFormUI)

const filterExistingCountries = (
  countries: CountryDto[] = [],
  countryOverrides: CountryOverride[] = [],
  tickmillCompanyId: number
) => {
  return countries.filter((country) => {
    const foundCountry = findCountryByCountryIdCompanyId(countryOverrides)(
      country.id,
      tickmillCompanyId
    )
    return !foundCountry?.country?.id
  })
}

const findCountryByCountryIdCompanyId =
  (countryOverrides: CountryOverride[] = []) =>
  (countryId: number | string, tickmillCompanyId: number | string) => {
    return countryOverrides.find((x) => {
      const hasCountry = countryId === x.country.id
      const hasCompany =
        Number.parseInt(tickmillCompanyId.toString(), 10) ===
        Number.parseInt(x.tickmillCompany.id.toString(), 10)

      return hasCountry && hasCompany
    })
  }
